import { MenuItem } from '@headlessui/react';
import { Check, Monitor, Moon, Sun } from 'lucide-react';
import { SegmentedToggle } from './ui';
import { useTheme, type ThemePreference } from './theme-context';

const OPTIONS: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
  { value: 'system', label: 'System', icon: Monitor },
];

/**
 * The full choice, for the settings screen: the two themes and "follow my
 * system". Both products draw the same one — the preference is per browser,
 * not per account, so there is nothing product-specific about it.
 */
export function ThemeSetting() {
  const { preference, setPreference } = useTheme();

  return (
    <div className="flex flex-col items-start gap-1.5">
      <SegmentedToggle
        value={preference}
        options={OPTIONS.map(({ value, label }) => ({ value, label }))}
        onChange={setPreference}
        ariaLabel="Appearance"
      />
      <p className="text-xs text-hilevel-gray">
        Saved in this browser, so each machine you sign in from can differ. Printed pages and
        documents stay light whichever you pick.
      </p>
    </div>
  );
}

/**
 * The same choice as rows in the user dropdown, which is where a signed-in
 * user looks for it.
 *
 * Three rows rather than one toggling row: they are `MenuItem`s, so they keep
 * the dropdown's own arrow-key navigation, and all three states are named —
 * an unlabelled control cannot say that "System" exists at all. The caller
 * passes its own row class because the two menu bars style theirs
 * differently.
 */
export function ThemeMenuItems({ itemClassName }: { itemClassName: string }) {
  const { preference, setPreference } = useTheme();

  return (
    <>
      {OPTIONS.map(({ value, label, icon: Icon }) => (
        <MenuItem key={value}>
          <button
            type="button"
            className={`${itemClassName} flex items-center gap-2`}
            aria-current={value === preference}
            onClick={() => setPreference(value)}
          >
            <Icon className="w-[1.25em] text-hilevel-gray" />
            {label}
            {value === preference && <Check className="ml-auto text-hilevel-red" />}
          </button>
        </MenuItem>
      ))}
    </>
  );
}

/**
 * The one-click form, for the menu bar when there is no dropdown to put the
 * rows in — which is to say, signed out.
 *
 * Only the two themes: somebody reaching for a single unlabelled button wants
 * the theme they can see to change now, and a three-way cycle says nothing
 * about what the next click does.
 */
export function ThemeToggleButton({ className }: { className?: string }) {
  const { resolved, setPreference } = useTheme();
  const next = resolved === 'dark' ? 'light' : 'dark';

  return (
    <button
      type="button"
      className={className}
      aria-label={`Switch to ${next} mode`}
      title={`Switch to ${next} mode`}
      onClick={() => setPreference(next)}
    >
      {resolved === 'dark' ? <Sun /> : <Moon />}
    </button>
  );
}
